import { isPending } from '@fleetcore/shared'
import { useTranslation } from 'react-i18next'

import { useFormatters } from '../../i18n/format'
import type { CapabilityProfile } from './api-types'
import { CHANNEL_REASON_KEY, type ChannelReading } from './capability-model'

/**
 * Turns a channel's absence into the sentence a gauge prints instead of a number.
 *
 * It lives apart from `capability-model.ts` because that module is pure and asserted
 * directly; this half needs the catalog and the locale-aware date formatter. The
 * split is what lets a test prove "stopped reporting" is derived correctly without
 * also pinning the French wording, and prove the wording without re-deriving the state.
 *
 * The date matters. "Ne remonte plus" is a symptom; "ne remonte plus depuis le
 * 14 mars 2026" is the beginning of a work order, because it tells a workshop which
 * service visit broke it.
 */
export function useChannelNotice(
  profile?: CapabilityProfile,
): (reading: ChannelReading) => string | undefined {
  const { t } = useTranslation()
  const formatters = useFormatters()

  return (reading) => {
    if (!isPending(reading.metric)) return undefined
    const key = reading.reasonKey
    if (key === null) return undefined

    if (key === CHANNEL_REASON_KEY.stopped) {
      return reading.lastSeenAt === null
        ? t('vehicles:capability.channel.stoppedUndated')
        : t(key, { when: formatters.date(reading.lastSeenAt) })
    }

    if (key === CHANNEL_REASON_KEY.outside_program) {
      const programNumber = profile?.canBus.programNumber ?? null
      return programNumber === null
        ? t('vehicles:capability.channel.outsideProgramUnknown')
        : t(key, { number: programNumber })
    }

    return t(key)
  }
}
